import type { Maybe } from '@adornis/base/utilTypes.js';
import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { confirmDialog, guardDialog } from '@adornis/dialog/dialog-functions.js';
import { DateInput } from '@adornis/formfield/fields/date-input.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { stateful, useMemo } from '@adornis/functional-lit/stateful.js';
import { hoverTooltip } from '@adornis/popover/hover-tooltip.js';
import { formatCalendarDay, toCalendarDay, todayAsCalendarDay } from '@adornis/datetime/calendarDay.js';
import { CardHeader } from '@adornis/components/client/CardHeader.js';
import { StatusBadge } from '@martinus/styles/client/StatusBadge.js';
import { html, nothing } from 'lit';
import { firstValueFrom } from 'rxjs';
import { useMartinusABAC } from '../../../permissions/client/useMartinusAbac.js';
import type { UserOnUserPayload } from '../../../users/UserPolicy.js';
import { ContractsActions } from '../../ContractsPolicy.js';
import { ContractAPI } from '../../api/ContractAPI.js';
import type { ContractMetaInformation } from '../../entities/ContractMetaInformation.js';
import { getContractStatusForDate, getSupplementStatusForDate } from '../../api/getSupplementStatus.js';
import { getReadableNameForContract } from '../../api/getReadableNameForContract.js';
import { Contract } from '../../entities/Contract.js';
import type { ContractSupplement } from '../../entities/ContractSupplement.js';
import { ContractTimelineBar } from './ContractTimelineBar.js';
import { CONTRACT_STATUS_PRESENTATION, SUPPLEMENT_STATUS_PRESENTATION } from './status/supplementStatusPresentation.js';
import { SupplementSelectionCard } from './SupplementSelectionCard.js';

export type TimelineSupplement = NarrowedEntity<
  ContractSupplement,
  {
    _id: 1;
    nachtragsNummer: 1;
    subNachtragsnummer: 1;
    automaticallyAssignedEndDateOfContract: 1;
    signedAt: 1;
    startDateGeneral: 1;
    endDateGeneral: 1;
    startDateAG1: 1;
    endDateAG1: 1;
    startDateAG2: 1;
    endDateAG2: 1;
    changes: { path: 1 };
  }
>;

export type TimelineContract = NarrowedEntity<
  Contract,
  {
    _id: 1;
    createdAt: 1;
    agInfos: { einstellungsdatum: 1; gekuendigtZum: 1; unterzeichnungsdatum: 1 };
    supplements: {
      _id: 1;
      nachtragsNummer: 1;
      subNachtragsnummer: 1;
      automaticallyAssignedEndDateOfContract: 1;
      signedAt: 1;
      startDateGeneral: 1;
      endDateGeneral: 1;
      startDateAG1: 1;
      endDateAG1: 1;
      startDateAG2: 1;
      endDateAG2: 1;
      changes: { path: 1 };
    };
  }
>;

interface ContractTimelineProps {
  loading?: boolean;
  /** a newer contract state is being fetched. Shown as a subtle hint, the controls stay usable. */
  refreshing?: boolean;
  isEditingSupplement?: boolean;
  message?: string;
  contracts: Maybe<TimelineContract[]>;
  selectedContract: Maybe<TimelineContract>;
  selectedDate: Date;
  simpleMode?: boolean;
  selectedSupplement: Maybe<TimelineSupplement>;
  onContractSelected: (contract: Maybe<TimelineContract>) => void;
  onSupplementSelected: (supplement: Maybe<TimelineSupplement>) => void;
  onDateSelected: (date: Date) => void;
  /** fired whenever a navigation action implicitly stops the supplement editing */
  onEditingSupplementStopped?: () => void;
}

type TimelineEntity = Maybe<TimelineContract | TimelineSupplement | 'unselect supplement'>;

function getPreviousEntity(props: ContractTimelineProps): TimelineEntity {
  if (!props.contracts) return null;
  if (!props.selectedContract) return props.contracts[0];

  const contractIndex = props.contracts.findIndex(c => c._id === props.selectedContract?._id);
  const supplementIndex = props.selectedContract.supplements?.findIndex(s => s._id === props.selectedSupplement?._id);

  // there is no way a contract is not selected, unless it is in an inital employee state and there are no contracts
  if (contractIndex === undefined) return null;

  // first contract no supplement selected => return nothing as there is no previous entity
  if (contractIndex === 0 && supplementIndex === -1) return null;

  // not first contract but no supplement selected => return previous contract
  // also only return the previous contract if we are not in simple mode. If we are we return null so the user cannot escape the contract
  if (contractIndex > 0 && supplementIndex === -1) return props.simpleMode ? null : props.contracts[contractIndex - 1];

  // first supplement selected => return unselect supplement
  if (supplementIndex === 0) return 'unselect supplement';

  // not first supplement selected => return previous supplement
  if (supplementIndex !== undefined && supplementIndex > 0)
    return props.selectedContract.supplements?.[supplementIndex - 1];

  return null;
}

function getNextEntity(props: ContractTimelineProps): TimelineEntity {
  if (!props.contracts || !props.selectedContract?.supplements?.length) return null;
  if (!props.selectedContract) return props.contracts[0];

  const contractIndex = props.contracts.findIndex(c => c._id === props.selectedContract?._id);
  const supplementIndex = props.selectedContract.supplements.findIndex(s => s._id === props.selectedSupplement?._id);

  // there is no way a contract is not selected, unless it is in an inital employee state and there are no contracts
  if (contractIndex === undefined) return null;

  // no supplements in last contract and last contract selected
  if (contractIndex === props.contracts.length - 1 && !props.selectedContract.supplements.length) return null;

  // last contract and last supplement selected => return nothing as there is no next entity
  if (contractIndex === props.contracts.length - 1 && supplementIndex === props.selectedContract.supplements.length - 1)
    return null;

  // no supplement selected => selecte first supplement
  if (supplementIndex === -1) return props.selectedContract.supplements[0];

  // last supplement selected => return next contract
  if (supplementIndex === props.selectedContract.supplements.length - 1) return props.contracts[contractIndex + 1];

  // not last supplement selected => return next supplement
  if (supplementIndex !== undefined && supplementIndex < props.selectedContract.supplements.length - 1)
    return props.selectedContract.supplements[supplementIndex + 1];

  return null;
}

/**
 * Navigation bar for browsing contracts, their supplements and the point in time for which the contract state is shown.
 * Fully controlled: state lives in the parent, changes are propagated via the callbacks.
 */
export const ContractTimeline = stateful((props: ContractTimelineProps) => {
  const { can: canDeleteContract } = useMartinusABAC<UserOnUserPayload>(
    { action: ContractsActions.DELETE_CONTRACT, viewedUser: null },
    [],
  );

  const stopEditing = () => props.onEditingSupplementStopped?.();

  /**
   * The supplements array is mutated in place when one is created or deleted, so its identity never changes and
   * the dropdown would keep its stale item list. Copying it whenever the count changes gives the dropdown a new
   * identity exactly when it needs one, without remounting it on every date or edit-mode change.
   */
  const supplementSelectables = useMemo(
    () => [[...((props.selectedContract?.supplements ?? []) as TimelineSupplement[])]],
    [props.selectedContract?._id, props.selectedContract?.supplements?.length],
  );

  // content only, the dropdown row brings its own padding, hover and selection colors
  const renderContractItem = (c: TimelineContract) => {
    // evaluated against the date the user is looking at, not against today
    const presentation = CONTRACT_STATUS_PRESENTATION[getContractStatusForDate(c, props.selectedDate)];
    return html`
      <div class="flex flex-row items-center justify-between gap-2">
        <span class="min-w-0 truncate">${getReadableNameForContract(c)}</span>
        <div class="flex shrink-0 flex-row items-center gap-1">
          ${StatusBadge({
            tone: presentation.tone,
            label: presentation.label,
            iconClass: presentation.iconClass,
            title: presentation.description,
          })}
          ${
            canDeleteContract
              ? html`
                  <button
                    class="btn btn-ghost btn-xs btn-square"
                    aria-label="Vertrag löschen"
                    @mousedown=${(e: Event) => {
                      // the dropdown row picks on mousedown and closes the popover, so the click would never land here
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    @click=${async (e: Event) => {
                      e.preventDefault();
                      e.stopPropagation();

                      const confirm = await confirmDialog(
                        `Sind Sie sicher, dass Sie den Vertrag löschen wollen? Dieser Vorgang kann nicht rückgängig gemacht werden.`,
                        { highlightDecline: true },
                      );
                      if (!confirm) return;

                      await guardDialog(async () => {
                        const id = c._id;
                        await (c as Contract).remove();
                        props.onContractSelected(null);

                        const meta = (await firstValueFrom(
                          ContractAPI.subscribeMeta(id)({ _id: 1 }),
                        )) as Maybe<ContractMetaInformation>;
                        await meta?.remove();

                        props.onSupplementSelected(null);
                        location.reload();
                      });
                    }}
                  >
                    <span class="icon-[material-symbols--delete-outline] text-error text-base"></span>
                  </button>
                `
              : nothing
          }
        </div>
      </div>
    `;
  };

  const renderSupplementItem = (c: TimelineSupplement) => html`
    ${SupplementSelectionCard({
      supplement: c,
      date: props.selectedDate,
      overrideInactive: props.isEditingSupplement
        ? c.nachtragsNummer > (props.selectedSupplement?.nachtragsNummer ?? 0)
        : !!(c.automaticallyAssignedEndDateOfContract && c.automaticallyAssignedEndDateOfContract < props.selectedDate),
      overrideActive: props.isEditingSupplement
        ? c.nachtragsNummer === (props.selectedSupplement?.nachtragsNummer ?? 0) &&
          (!c.subNachtragsnummer || c.subNachtragsnummer === (props.selectedSupplement?.subNachtragsnummer ?? 0))
        : false,
    })}
  `;

  const previousEntity = getPreviousEntity(props);
  const nextEntity = getNextEntity(props);

  const isTodaySelected = toCalendarDay(props.selectedDate).getTime() === todayAsCalendarDay().getTime();

  const selectedContractStatus = props.selectedContract
    ? CONTRACT_STATUS_PRESENTATION[getContractStatusForDate(props.selectedContract, props.selectedDate)]
    : null;

  // an old contract can look "Aktiv" just because the selected date lies inside its runtime. Showing the status
  // at today next to it makes immediately visible that the user is looking at a past (or future) state.
  const contractStatusToday =
    props.selectedContract && !isTodaySelected
      ? CONTRACT_STATUS_PRESENTATION[getContractStatusForDate(props.selectedContract, todayAsCalendarDay())]
      : null;

  const selectedSupplementStatus = props.selectedSupplement
    ? SUPPLEMENT_STATUS_PRESENTATION[
        getSupplementStatusForDate(props.selectedSupplement, props.selectedDate, {
          isBeingEdited: props.isEditingSupplement,
        }).status
      ]
    : null;

  return html`
    <!-- flex-none: DaisyUI's card-body is flex:auto and would take the free height of the contract page -->
    <div class="card card-body flex-none">
      ${CardHeader({
        title: 'Timeline',
        subtitle: html`
          <span class="flex flex-row flex-wrap items-center gap-2">
            ${
              contractStatusToday
                ? html`
                    <span class="flex flex-row items-center gap-1">
                      <span class="text-base-content/70">Vertrag heute</span>
                      ${StatusBadge({
                        tone: contractStatusToday.tone,
                        label: contractStatusToday.label,
                        iconClass: contractStatusToday.iconClass,
                        title: 'Status des Vertrags am heutigen Tag.',
                      })}
                    </span>
                  `
                : nothing
            }
            ${
              selectedContractStatus
                ? html`
                    <span class="flex flex-row items-center gap-1">
                      <span class="text-base-content/70">
                        ${
                          isTodaySelected
                            ? 'Vertrag'
                            : `Vertrag am ${formatCalendarDay(props.selectedDate, 'dd.MM.yyyy')}`
                        }
                      </span>
                      ${StatusBadge({
                        tone: selectedContractStatus.tone,
                        label: selectedContractStatus.label,
                        iconClass: selectedContractStatus.iconClass,
                        title: selectedContractStatus.description,
                      })}
                    </span>
                  `
                : nothing
            }
            ${
              selectedSupplementStatus
                ? html`
                    <span class="flex flex-row items-center gap-1">
                      <span class="text-base-content/70">Nachtrag</span>
                      ${StatusBadge({
                        tone: selectedSupplementStatus.tone,
                        label: selectedSupplementStatus.label,
                        iconClass: selectedSupplementStatus.iconClass,
                        title: selectedSupplementStatus.description,
                      })}
                    </span>
                  `
                : nothing
            }
          </span>
        `,
        actionsDivider: true,
        actions: html`
          ${
            props.refreshing
              ? html`<span class="loading loading-spinner loading-xs text-base-content/70" aria-label="Lädt"></span>`
              : nothing
          }
          ${props.message ? html`<span class="font-semibold text-warning">${props.message}</span>` : nothing}
          ${
            isTodaySelected
              ? nothing
              : StatusBadge({
                  tone: 'warning',
                  label: `Stand vom ${formatCalendarDay(props.selectedDate, 'dd.MM.yyyy')}`,
                  iconClass: 'icon-[material-symbols--warning-rounded]',
                  title: 'Das gewählte Datum ist nicht heute. Alle Angaben zeigen den Vertragsstand zu diesem Datum.',
                })
          }
        `,
      })}
      ${
        props.loading
          ? html`<div class="skeleton h-10 w-full"></div>`
          : html`
              <div class="flex flex-col md:flex-row gap-2 md:items-center">
                ${
                  props.simpleMode
                    ? nothing
                    : html`
                        <div class="flex-1 min-w-0">
                          ${DropdownSelectionInput({
                            value: props.selectedContract ?? undefined,
                            placeholder: 'Vertrag auswählen',
                            selectables: props.contracts ?? [],
                            resolveID: (c: TimelineContract) => c._id,
                            renderString: (c: TimelineContract) => getReadableNameForContract(c),
                            renderItem: renderContractItem,
                            // only the contract callback: it already resets the supplement in the parent. Calling
                            // both would start a second recalculation from the not yet applied state and let it
                            // win the race, showing the previously selected contract in the form.
                            onPicked: (c: TimelineContract | null) => {
                              if (!c || c._id === props.selectedContract?._id) return;
                              props.onContractSelected(c);
                            },
                          })}
                        </div>
                        <div class="hidden md:block w-0.5 self-stretch bg-base-300"></div>
                      `
                }

                <div class="flex flex-1 flex-row items-center gap-1 min-w-0">
                  ${
                    props.selectedContract?.agInfos?.einstellungsdatum &&
                    props.selectedDate.getTime() !== props.selectedContract.agInfos.einstellungsdatum.getTime()
                      ? html`
                          <div class="tooltip" ${hoverTooltip()}>
                            <div class="tooltip-content">Zum Vertragsanfang springen</div>
                            <button
                              class="btn btn-ghost btn-sm btn-square"
                              @click=${() => {
                                if (!props.selectedContract?.agInfos?.einstellungsdatum) return;
                                props.onDateSelected(props.selectedContract.agInfos.einstellungsdatum);
                              }}
                            >
                              <span class="icon-[material-symbols--first-page] text-lg"></span>
                            </button>
                          </div>
                        `
                      : nothing
                  }
                  <div class="flex-1 min-w-0">
                    ${DateInput({
                      value: props.selectedDate,
                      placeholder: 'Vertragsstatus zum',
                      placeholderMode: 'prefix',
                      min: props.selectedContract
                        ? formatCalendarDay(toCalendarDay(props.selectedContract.createdAt), 'yyyy-MM-dd')
                        : undefined,
                      onPicked: (date: Date | null) => {
                        if (!date) return;
                        props.onDateSelected(date);
                      },
                    })}
                  </div>
                  ${
                    !isTodaySelected
                      ? html`
                          <div class="tooltip" ${hoverTooltip()}>
                            <div class="tooltip-content">Auf "Heute" zurücksetzen</div>
                            <button
                              class="btn btn-ghost btn-sm btn-square"
                              aria-label="Auf Heute zurücksetzen"
                              @click=${() => {
                                // a plain `new Date()` carries the time of day, which would no longer match the
                                // canonical day and would put the whole view into the readonly state
                                props.onDateSelected(todayAsCalendarDay());
                                props.onSupplementSelected(null);
                                stopEditing();
                              }}
                            >
                              <span class="icon-[material-symbols--restart-alt] text-lg"></span>
                            </button>
                          </div>
                        `
                      : nothing
                  }
                </div>

                <div class="hidden md:block w-0.5 self-stretch bg-base-300"></div>

                <div class="flex flex-1 flex-row items-center gap-1 min-w-0">
                  ${
                    previousEntity !== null && previousEntity !== undefined
                      ? html`
                          <button
                            class="btn btn-ghost btn-sm btn-square"
                            aria-label="Vorheriger Eintrag"
                            @click=${() => {
                              if (previousEntity === 'unselect supplement') {
                                props.onSupplementSelected(null);
                              } else if (previousEntity instanceof Contract) {
                                props.onContractSelected(previousEntity);
                                props.onSupplementSelected(previousEntity.supplements?.at(-1) ?? null);
                              } else {
                                props.onSupplementSelected(previousEntity as TimelineSupplement);
                              }

                              stopEditing();
                            }}
                          >
                            <span class="icon-[material-symbols--chevron-left] text-lg"></span>
                          </button>
                        `
                      : html`<div></div>`
                  }
                  <div class="flex-1 min-w-0">
                    ${DropdownSelectionInput({
                      value: props.selectedSupplement ?? undefined,
                      clearable: true,
                      placeholder: 'Nachtrag zum Bearbeiten',
                      selectables: supplementSelectables,
                      resolveID: (c: TimelineSupplement) => c._id,
                      renderString: (c: TimelineSupplement) =>
                        `${c.nachtragsNummer}${c.subNachtragsnummer ? `.${c.subNachtragsnummer}` : ''}`,
                      renderItem: renderSupplementItem,
                      onPicked: (c: TimelineSupplement | null) => {
                        props.onSupplementSelected(c ?? null);
                        stopEditing();
                      },
                    })}
                  </div>
                  ${
                    nextEntity !== null && nextEntity !== undefined
                      ? html`
                          <button
                            class="btn btn-ghost btn-sm btn-square"
                            aria-label="Nächster Eintrag"
                            @click=${() => {
                              if (nextEntity === 'unselect supplement') {
                                props.onSupplementSelected(null);
                              } else if (nextEntity instanceof Contract) {
                                props.onContractSelected(nextEntity);
                                props.onSupplementSelected(null);
                              } else {
                                props.onSupplementSelected(nextEntity as TimelineSupplement);
                              }

                              stopEditing();
                            }}
                          >
                            <span class="icon-[material-symbols--chevron-right] text-lg"></span>
                          </button>
                        `
                      : html`<div></div>`
                  }
                </div>
              </div>

              ${ContractTimelineBar({
                contract: props.selectedContract,
                selectedDate: props.selectedDate,
                selectedSupplement: props.selectedSupplement,
                isEditingSupplement: props.isEditingSupplement,
                onDateSelected: props.onDateSelected,
                onSupplementSelected: supplement => {
                  props.onSupplementSelected(supplement);
                  stopEditing();
                },
              })}
            `
      }
    </div>
  `;
});
